"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import makeStyles from "../../theming/make-styles.js";
import Attachment from "../attachment/index.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
let OPEN_STACK = [];
const useStyles = makeStyles()((theme, { block, color }) => {
    const palette = theme.palette(color).dropdownBase;
    return {
        attachment: {
            backgroundColor: palette.attachment.backgroundColor,
            border: `${theme.size(1)} solid ${palette.attachment.borderColor}`,
            borderRadius: theme.size(4),
            boxShadow: theme.shadow("sm"),
            outline: 0,
        },
        wrapper: {
            display: block ? "grid" : undefined,
            flexGrow: block ? 1 : undefined,
        },
    };
});
export function contains(element, ...containers) {
    if (!element)
        return false;
    for (const container of containers) {
        if (!container)
            continue;
        for (let el = element; !!el; el = el.parentElement) {
            if (el === container)
                return true;
        }
    }
    return false;
}
export const DropdownBase = React.forwardRef(function DropdownBase(_a, ref) {
    var { attachment: AttachmentComponent = Attachment, block = false, children, className, color = "primary", openRef, strategy, triggerElement, triggerStrategy = "open", onBlur, onClose, onOpen } = _a, props = __rest(_a, ["attachment", "block", "children", "className", "color", "openRef", "strategy", "triggerElement", "triggerStrategy", "onBlur", "onClose", "onOpen"]);
    const [open, setOpen] = useState(false);
    const attachmentRef = useRef(null);
    const { classes, cx } = useStyles({ block, color });
    const id = useMemo(() => uuid(), []);
    const innerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const handleToggleOpen = useCallback((newOpen) => {
        if (open !== newOpen) {
            setOpen(newOpen);
            if (newOpen) {
                OPEN_STACK.push(id);
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            }
            else {
                OPEN_STACK = OPEN_STACK.filter(i => i !== id);
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
        }
    }, [id, open, onClose, onOpen]);
    if (openRef) {
        openRef.current = { open, setOpen: handleToggleOpen };
    }
    const handleClose = useCallback(() => handleToggleOpen(false), [handleToggleOpen]);
    const handleBlur = useCallback((e) => {
        var _a, _b;
        if ("onBlur" in triggerElement.props) {
            (_b = (_a = triggerElement.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        if (OPEN_STACK[OPEN_STACK.length - 1] !== id) {
            return;
        }
        if (contains(e.relatedTarget instanceof Element ? e.relatedTarget : null, innerRef.current, attachmentRef.current)) {
            return;
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        handleClose();
    }, [handleClose, id, onBlur, triggerElement]);
    const handleClick = useCallback(function handleClick(e) {
        var _a, _b;
        e.preventDefault();
        e.stopPropagation();
        if ("onClick" in triggerElement.props) {
            (_b = (_a = triggerElement.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        handleToggleOpen(triggerStrategy === "open" ? true : !open);
    }, [handleToggleOpen, open, triggerElement.props, triggerStrategy]);
    return (_jsxs("div", Object.assign({ className: cx(classes.wrapper, className), "data-component": "dropdown-base" }, props, { children: [React.cloneElement(triggerElement, {
                "ref": combinedRef,
                "aria-expanded": open,
                "aria-haspopup": "menu",
                "tabIndex": 0,
                "onBlur": handleBlur,
                "onClick": handleClick,
            }), open && (_jsx(AttachmentComponent, { ref: attachmentRef, anchorRef: combinedRef, className: classes.attachment, strategy: strategy, tabIndex: 0, onBlur: handleBlur, onClose: handleClose, children: children }))] })));
});
export default DropdownBase;
//# sourceMappingURL=dropdown-base.js.map